import { resultError, resultOk, type Result } from '../result'
import log from 'loglevel'
import type { Capabilities } from './capabilities'
import type { JsBridgeMiniOfA } from './jsBridge'
import * as Sentry from '@sentry/vue'
import type { DeviceApiClientName } from './clientName'

/**
 * Thrown when web client has implemented support for this method but native
 * client does not support it.
 */
export class NotExposedByDeviceApiNativeError extends Error {}

/**
 * NOTE: when interacting with maybeDeviceApi, make sure you don't
 * call references to specific functions from that object, but call those
 * functions on that object exclusively.
 *
 * Call on a reference results in function receiving unexpected `this` causing
 * a runtime error.
 *
 * Examples:
 *
 * Invalid call on reference looks like this:
 *
 * ```ts
 * const getClientName = window.mobilemissions.getClientName
 * getClientName()
 * ```
 *
 * A valid call looks like this:
 *
 * ```ts
 * window.mobilemissions.getClientName()
 * ```
 *
 */
export class DeviceApiNative {
  readonly type = 'native'

  constructor(private jsBridge: Partial<JsBridgeMiniOfA>) {}

  private reportMethodNotExposed(methodName: string, clientName?: string) {
    Sentry.captureMessage(
      `Tried to use ${methodName} method from DeviceApi but client ${clientName ?? 'unknown'} does not expose it.`
    )
  }

  private reportMethodCallError(methodName: string, error: unknown) {
    Sentry.captureException(error)
    log.debug(`An error has occurred when calling DeviceApi.${methodName}(): ${error}`)
  }

  getClientName(): DeviceApiClientName {
    if (this.jsBridge.getClientName === undefined) {
      this.reportMethodNotExposed('getClientName')
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge.getClientName()
    } catch (error: unknown) {
      this.reportMethodCallError('getClientName', error)
      return 'unknown'
    }
  }

  getClientNameAsResult(): Result<DeviceApiClientName> {
    try {
      return resultOk(this.getClientName())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  getClientLocalization(): string {
    if (this.jsBridge.getClientLocalization === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge.getClientLocalization()
    } catch (error: unknown) {
      this.reportMethodCallError('getClientLocalization', error)
      return ''
    }
  }

  getClientVersion(): string {
    if (this.jsBridge.getClientVersion === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge.getClientVersion()
    } catch (error: unknown) {
      this.reportMethodCallError('getClientVersion', error)
      return ''
    }
  }

  getCapabilities(): Capabilities {
    if (this.jsBridge.getCapabilities === undefined) {
      // NOTE: fallbacks to empty object if client does not expose getCapabilities.
      return {}
    }

    try {
      return this.jsBridge.getCapabilities()
    } catch (error: unknown) {
      this.reportMethodCallError('getCapabilities', error)
      return {}
    }
  }

  requestAccessToken(forceRefresh: boolean): void {
    if (this.jsBridge.requestAccessToken === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this?.jsBridge?.requestAccessToken?.(forceRefresh)
    } catch (error: unknown) {
      this.reportMethodCallError('getCapabilities', error)
    }
  }

  onTokensChange(access: string, refresh: string): void {
    if (this.jsBridge.onTokensChange === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.onTokensChange?.(access, refresh)
    } catch (error: unknown) {
      this.reportMethodCallError('onTokensChange', error)
    }
  }

  onLogout(): void {
    if (this.jsBridge.onLogout === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.onLogout?.()
    } catch (error: unknown) {
      this.reportMethodCallError('onLogout', error)
    }
  }

  getWalletAddress(): string | undefined {
    if (this.jsBridge.getWalletAddress === undefined) {
      return undefined
    }

    try {
      return this.jsBridge?.getWalletAddress?.()
    } catch (error: unknown) {
      this.reportMethodCallError('getWalletAddress', error)
      return undefined
    }
  }

  openOperaMiniDeepLink(deepLink: string, close: boolean): void {
    if (this.jsBridge.openOperaMiniDeepLink == undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.openOperaMiniDeepLink?.(deepLink, close)
    } catch (error: unknown) {
      this.reportMethodCallError('openOperaMiniDeepLink', error)
    }
  }

  close(): void {
    if (this.jsBridge.close === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.close?.()
    } catch (error: unknown) {
      this.reportMethodCallError('close', error)
    }
  }

  requestSocialLogin(): void {
    if (this.jsBridge.requestSocialLogin === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.requestSocialLogin?.()
    } catch (error: unknown) {
      this.reportMethodCallError('requestSocialLogin', error)
    }
  }

  getSocialAccount(): string | undefined {
    try {
      return this.jsBridge.getSocialAccount?.()
    } catch (error: unknown) {
      this.reportMethodCallError('getSocialAccount', error)
      return undefined
    }
  }

  socialSignOut(): void {
    try {
      return this.jsBridge.socialSignOut?.()
    } catch (error: unknown) {
      this.reportMethodCallError('socialSignOut', error)
    }
  }

  async isDefaultBrowser(): Promise<boolean | undefined> {
    if (this.jsBridge.isDefaultBrowser === undefined) {
      this.reportMethodNotExposed('isDefaultBrowser', this.getClientName())
      return Promise.resolve(undefined)
    }

    try {
      return Promise.resolve(this.jsBridge.isDefaultBrowser())
    } catch (error: unknown) {
      this.reportMethodCallError('isDefaultBrowser', error)
      return Promise.resolve(undefined)
    }
  }

  getKilobytesSavedToday(): number {
    if (this.jsBridge.getKilobytesSavedToday === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge.getKilobytesSavedToday()
    } catch (error: unknown) {
      this.reportMethodCallError('getKilobytesSavedToday', error)
      return 0
    }
  }

  getKilobytesSavedTodayAsResult(): Result<number> {
    try {
      return resultOk(this.getKilobytesSavedToday())
    } catch (error: unknown) {
      return resultError(error)
    }
  }

  triggerDataSavedReport(): void {
    if (this.jsBridge.triggerDataSavedReport === undefined) {
      throw new NotExposedByDeviceApiNativeError()
    }

    try {
      return this.jsBridge?.triggerDataSavedReport?.()
    } catch (error: unknown) {
      this.reportMethodCallError('triggerDataSavedReport', error)
    }
  }

  getAccessToken(_forceRefresh: Boolean): Promise<string> {
    throw new NotExposedByDeviceApiNativeError()
  }
}
